import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { Menu } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { ActionMenuList, ActionMenuRow } from './messageActionMenu';

export type CallMode = 'voice' | 'video';

/**
 * The conversation-level call control. Starting a call opens a recorded room
 * straight away, so the press only opens a menu and the choice of voice or video
 * is what starts it.
 */
export const CallModeButton = ({
  onStart,
  testID,
}: {
  onStart: (mode: CallMode) => void;
  testID?: string;
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const start = (mode: CallMode) => {
    setVisible(false);
    onStart(mode);
  };

  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      contentStyle={styles.menu}
      anchor={
        <Pressable
          onPress={() => setVisible(true)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('issue.menu.call')}
          testID={testID}
        >
          <Icon name="Call" size={20} color={Colors.role.textStrong} />
        </Pressable>
      }
    >
      <ActionMenuList>
        <ActionMenuRow
          iconName="Call"
          label={t('chat.call.voice')}
          accessibilityLabel={t('chat.call.voice')}
          onPress={() => start('voice')}
          testID={testID ? `${testID}-voice` : undefined}
        />
        <ActionMenuRow
          iconName="Video"
          label={t('chat.call.video')}
          accessibilityLabel={t('chat.call.video')}
          onPress={() => start('video')}
          testID={testID ? `${testID}-video` : undefined}
        />
      </ActionMenuList>
    </Menu>
  );
};

const styles = themedStyleSheet((role) => ({
  menu: {
    backgroundColor: role.surfacePopover,
    borderRadius: tokens.borderRadius.md,
    paddingVertical: 0,
  },
}));
